<script>
import { Helper } from '@/mixin/helper'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import FormDeviceProfile from './FormDeviceProfile'
import * as vssConfiguration from '@/services/rest-api-sms/lib/vss-configuration/vss-configuration'
import { transformToPayload } from './lib/vssconfiguration'
import { getToolsClient } from '@/services/tools'

const toolsAxios = getToolsClient()

export default {
  name: 'DeviceProfileSettings',
  components: {
    FormDeviceProfile,
    Layout,
    PageHeader,
  },
  mixins: [Helper],
  data() {
    return {
      // Table data
      profiles: [],
      filteredProfiles: [],
      filteredItems: [],
      selectedProfiles: [],

      // Pagination
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      totalPages: 0,

      // Filters
      filters: {
        profileName: null,
        deviceType: null,
        action: null,
      },
      
      // Options
      deviceTypeOptions: [],
      deviceTypeMap: {},

      actionOptions: [
        { id: 'adas', name: 'ADAS' },
        { id: 'dms', name: 'DMS' },
      ],
      
      // Table fields
      fields: [
        { key: 'selected', label: '' },
        { key: 'profile_name', label: 'Profile Name', sortable: true },
        { key: 'device_code', label: 'Device Type', sortable: true },
        { key: 'type', label: 'Action Type', sortable: true },
        { key: 'created_at', label: 'Created At', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      
      // Modals
      showFormModal: false,
      
      // Form data
      formData: null,
      
      // Loading states
      isLoading: false,
      isFormLoading: false,
      isDeleting: false,
      
      // Region
      region: 'SG',

      // Delete modal
      showDeleteModal: false,
    }
  },
  computed: {
    title() {
      return 'Profile ADAS dan DMS'
    },
    allSelected() {
      return this.filteredProfiles.length > 0 && this.selectedProfiles.length === this.filteredProfiles.length
    },
    someSelected() {
      return this.selectedProfiles.length > 0 && !this.allSelected
    },
  },
  mounted() {
    this.getDeviceTypes()
    this.loadProfiles()
  },
  methods: {
    async loadProfiles() {
      this.isLoading = true
      try {
        await this.applyFilters()
      } catch (error) {
        this.$toast.error('Failed to load profiles')
        this.profiles = []
      } finally {
        this.isLoading = false
      }
    },

    async getDeviceTypes() {
      const { data } = await toolsAxios.get('/qcdevice/devicetypes')
      this.deviceTypeOptions = data
        .filter(({ device_name }) => device_name.includes('VSS'))
        .map(({ device_code, device_name }) => {
          this.deviceTypeMap[device_code] = device_name
          return { id: device_code, name: device_name }
        })
    },
    
    async applyFilters() {
      const params = {
        filters: [],
        limit: this.perPage,
        page: this.currentPage,
        search: this.filters.profileName,
        sorts: ['created_at desc']
      }

      if (this.filters.action) {
        params.filters.push(`type eq '${this.filters.action}'`)
      }

      if (this.filters.deviceType) {
        params.filters.push(`device_code eq ${this.filters.deviceType}`)
      }

      const response = await vssConfiguration.get(params, this.region)

      this.filteredProfiles = response?.data?.paginated_result || []
      this.filteredItems = response?.data?.paginated_result || []
      this.totalRows = response?.metadata?.total_count || 0
      this.totalPages = response?.metadata?.total_page || 0
      this.currentPage = response?.metadata?.page || 1
      this.selectedProfiles = []
    },

    changePage(e) {
      this.currentPage = e
      this.applyFilters()
    },
    
    resetFilters() {
      this.filters = {
        profileName: null,
        deviceType: null,
        action: null,
      }
      this.applyFilters()
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedProfiles = []
      } else {
        this.selectedProfiles = [...this.filteredProfiles]
      }
    },

    isSelected(item) {
      return this.selectedProfiles.some(p => p.id === item.id)
    },

    toggleSelect(item) {
      const index = this.selectedProfiles.findIndex(p => p.id === item.id)
      if (index > -1) {
        this.selectedProfiles.splice(index, 1)
      } else {
        this.selectedProfiles.push(item)
      }
    },

    async deleteSelected() {
      if (this.selectedProfiles.length === 0) return

      this.showDeleteModal = true
    },

    async confirmDelete() {
      this.isDeleting = true
      try {
        const ids = this.selectedProfiles.map((selected) => selected.id)
        await vssConfiguration.deleteProfile({ ids }, this.region)

        this.$toast.success(`Successfully deleted ${this.selectedProfiles.length} profile(s)`)
        this.selectedProfiles = []
        this.showDeleteModal = false
        await this.applyFilters()
      } catch (error) {
        this.$toast.error('Failed to delete profiles')
      } finally {
        this.isDeleting = false
      }
    },

    cancelDelete() {
      this.showDeleteModal = false
    },
    
    openCreateModal() {
      this.formData = null
      this.showFormModal = true
    },
    
    openEditModal(item) {
      this.formData = { ...item }
      this.showFormModal = true
    },
    
    async handleFormSubmit(formData) {
      this.isFormLoading = true
      
      try {
        const payload = transformToPayload(formData)
        
        if (formData.id) {
          await vssConfiguration.updateProfile(formData.id, payload, this.region)
        } else {
          await vssConfiguration.createProfile(payload, this.region)
        }
        
        await this.applyFilters()
        this.showFormModal = false
      
        this.$toast.success('Profile saved successfully')
      } finally {
        this.isFormLoading = false
      }
    },
  },
}
</script>

<template>
    <Layout>
      <PageHeader :title="title" />
    <div class="card mb-3">
      <div class="card-body">
        <b-row class="mb-3">
          <b-col cols="12" md="3" class="mb-2">
            <label>Profile Name</label>
            <b-form-input
              v-model="filters.profileName"
              placeholder="Search by profile name"
              @input="applyFilters"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <label>Device Type</label>
            <v-select
              v-model="filters.deviceType"
              :options="deviceTypeOptions"
              label="name"
              :reduce="v => v.id"
              placeholder="Select device type"
              @input="applyFilters"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <label>Action</label>
            <v-select
              v-model="filters.action"
              :options="actionOptions"
              label="name"
              :reduce="v => v.id"
              placeholder="Select action"
              @input="applyFilters"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-2 d-flex align-items-end justify-content-end">
            <b-button variant="outline-secondary" @click="resetFilters" class="me-2">
              Reset
            </b-button>
            <b-button 
              v-if="selectedProfiles.length > 0"
              variant="danger" 
              @click="deleteSelected" 
              class="me-2"
            >
              <i class="mdi mdi-delete"></i> Delete 
            </b-button>
            <b-button variant="primary" @click="openCreateModal">
              <i class="mdi mdi-plus"></i> Create Profile
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <b-table
          :items="filteredProfiles"
          :fields="fields"
          :busy="isLoading"
          class="mb-3"
          responsive
          striped
          hover
          show-empty
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong class="ms-2">Loading...</strong>
            </div>
          </template>

          <template #empty>
            <div class="text-center">No profiles found</div>
          </template>

          <template #head(selected)>
            <b-form-checkbox
              :checked="allSelected"
              :indeterminate="someSelected"
              @change="toggleSelectAll"
            />
          </template>

          <template #cell(selected)="{ item }">
            <b-form-checkbox
              :checked="isSelected(item)"
              @change="toggleSelect(item)"
            />
          </template>

          <template #cell(created_at)="data">
            {{ data.item.created_at ? momentDMHms(data.value) : '-' }}
          </template>

          <template #cell(device_code)="{ item }">
            {{ deviceTypeMap[item.device_code] || '-' }}
          </template>

          <template #cell(type)="{ item }">
            {{ item.type ? item.type.toUpperCase() : '-' }}
          </template>

          <template #cell(actions)="{ item }">
            <b-button
              size="sm"
              variant="info"
              @click="openEditModal(item)"
            >
              <i class="mdi mdi-pencil"></i>
            </b-button>
          </template>
        </b-table>
        
        <div class="row">
          <div class="col-sm-12 col-md-6">
            <div id="tickets-table_length" class="dataTables_length">
              <label class="d-inline-flex align-items-center">
                <span>Menampilkan</span>
                <span>
                  <b-form-select
                    class="form-select form-select-sm"
                    v-model="perPage"
                    size="sm"
                    :options="pageOptions"
                    @change="resetFilters"
                  />
                </span>
                <span>entri.&nbsp;&nbsp;</span>
                <span
                  >Menampilkan&nbsp;
                  <strong>
                    {{ filteredItems?.length > 0 ? (currentPage - 1) * perPage + 1 : 0 }}
                  </strong>
                  &nbsp;sampai&nbsp;
                  <strong>
                    {{
                      currentPage != totalPages
                        ? currentPage * perPage
                        : (currentPage - 1) * perPage + filteredItems?.length
                    }}
                  </strong>
                  &nbsp;dari&nbsp;
                  <strong>{{ totalRows }}</strong>
                  &nbsp;entri.
                </span>
              </label>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <div class="dataTables_paginate paging_simple_numbers float-end">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  @change="changePage"
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FormDeviceProfile
      :modal.sync="showFormModal"
      :isLoading.sync="isFormLoading"
      :initialData="formData"
      :deviceTypeOptions="deviceTypeOptions"
      @submit="handleFormSubmit"
    />

    <b-modal
      v-model="showDeleteModal"
      title="Confirm Delete"
      @ok="confirmDelete"
      @cancel="cancelDelete"
      ok-variant="danger"
      ok-title="Delete"
      cancel-title="Cancel"
    >
      <p>Are you sure you want to delete <strong>{{ selectedProfiles.length }}</strong> profile(s)?</p>
      
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel" :disabled="isDeleting">
          Cancel
        </b-button>
        <b-button variant="danger" @click="ok" :disabled="isDeleting">
          <b-spinner v-if="isDeleting" small class="me-1"></b-spinner>
          {{ isDeleting ? 'Deleting...' : 'Delete' }}
        </b-button>
      </template>
    </b-modal>
  </Layout>
</template>
